import { CALENDAR_MESSAGES, ICS_PROVIDER, icalUrlSchema } from '@fos/shared/contracts/calendar';
import type { IntegrationItemDto } from '@fos/shared/contracts/integrations';
import { CALENDAR_MEETING_TEXTS, ICS_CALENDAR_TITLE, ICS_FORM_TEXTS } from '@fos/shared/labels';
import { type FormEvent, useEffect, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { PasswordInput } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';
import { testResultToast } from './integrations-ui';

/**
 * Okno „Kalendarz (link iCal)” (Ustawienia → Integracje; DECISIONS 2026-10-07 „Bez Google Workspace”): pole „Tajny
 * adres w formacie iCal” (typ hasła — adres to sekret: nigdy nie wypełniane zapisaną wartością, serwer jej nie zwraca,
 * czyszczone przy zamknięciu), instrukcja, skąd go wziąć w Kalendarzu Google, informacja o prywatności. Po zapisie:
 * test (pobranie i liczba wydarzeń w oknie, bez zapisu spotkań), a gdy działa — pierwsza synchronizacja w tle.
 * Zapis wymaga potwierdzenia hasłem (modal step-up z linku tRPC).
 */
export function IcsCalendarDialog({
  open,
  onOpenChange,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: IntegrationItemDto;
}) {
  const utils = trpc.useUtils();
  const switchMode = trpc.integrations.switchMode.useMutation();
  const test = trpc.integrations.test.useMutation();
  const syncNow = trpc.calendar.syncNow.useMutation();
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const keeps = item.id !== null && item.provider === ICS_PROVIDER && item.hasSecret;

  // biome-ignore lint/correctness/useExhaustiveDependencies: czyszczenie adresu i błędów przy każdym otwarciu i zamknięciu
  useEffect(() => {
    setUrl('');
    setError(null);
    setFormError(null);
  }, [open]);

  const afterSave = async (id: string) => {
    await utils.integrations.list.invalidate();
    try {
      const result = await test.mutateAsync({ id });
      testResultToast(result);
      if (result.ok) {
        await syncNow.mutateAsync({ id });
        toast.success(CALENDAR_MEETING_TEXTS.syncQueued);
      }
    } catch (err) {
      if (appErrorCode(err) !== 'STEP_UP_REQUIRED') toast.error(errorMessage(err));
    } finally {
      await utils.integrations.list.invalidate();
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFormError(null);
    const input: Record<string, unknown> = item.id
      ? { id: item.id, version: item.version ?? undefined, mode: 'real', provider: ICS_PROVIDER }
      : { kind: item.kind, mode: 'real', provider: ICS_PROVIDER };
    if (url.trim() || !keeps) {
      const parsed = icalUrlSchema.safeParse(url);
      if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? CALENDAR_MESSAGES.urlRequired);
      input.icalUrl = parsed.data;
    }
    try {
      const saved = await switchMode.mutateAsync(input as never);
      setUrl('');
      onOpenChange(false);
      toast.success('Zapisano kalendarz');
      if (saved.id) await afterSave(saved.id);
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'STEP_UP_REQUIRED') return setFormError('Zmiana wymaga potwierdzenia hasłem. Spróbuj ponownie.');
      const issue = validationIssues(err).find((i) => i.path === 'icalUrl');
      if (issue) return setError(issue.message);
      setFormError(
        code === 'CONFLICT' ? 'Ktoś zmienił tę integrację. Zamknij okno i spróbuj ponownie.' : errorMessage(err),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="m"
      title={ICS_CALENDAR_TITLE}
      description={ICS_FORM_TEXTS.intro}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={switchMode.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="ics-calendar-form" loading={switchMode.isPending}>
            Zapisz i sprawdź
          </Button>
        </>
      }
    >
      <form id="ics-calendar-form" onSubmit={submit} noValidate autoComplete="off" className="flex flex-col gap-4">
        <Field
          label={ICS_FORM_TEXTS.urlLabel}
          hint={keeps ? ICS_FORM_TEXTS.urlKeep : ICS_FORM_TEXTS.urlHint}
          error={error}
        >
          <PasswordInput
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
            placeholder={keeps ? 'Zapisany' : 'https://calendar.google.com/calendar/ical/…/basic.ics'}
          />
        </Field>
        <section aria-labelledby="ics-how-to" className="rounded-lg bg-surface px-4 py-3">
          <h3 id="ics-how-to" className="text-body-s font-semibold text-fg">
            {ICS_FORM_TEXTS.howToTitle}
          </h3>
          <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-body-s text-fg-secondary">
            {ICS_FORM_TEXTS.howTo.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p className="mt-2 text-caption text-fg-muted">{ICS_FORM_TEXTS.howToNote}</p>
        </section>
        <p className="text-body-s text-fg-secondary">{ICS_FORM_TEXTS.privacy}</p>
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
